import { useMemo, useState } from 'react'

import { local } from '../lib/sync'
import { unlocked } from '../lib/vault'
import { ACCESS_KIND_LABEL, type Access, type Client, type Contact } from '../types'
import { IconCopy, IconEye, IconPhone } from './icons'
import { Bar } from './TaskCard'
import { useLocal } from './useLocal'

export default function ClientCard({ clientId, onBack, onToast }: {
  clientId: number
  onBack: () => void
  onToast: (text: string) => void
}) {
  const { rows: clients } = useLocal<Client>(local.clients)
  const { rows: contacts } = useLocal<Contact>(local.contacts)
  const { rows: accesses } = useLocal<Access>(local.accesses)

  const client = useMemo(() => clients.find((c) => c.id === clientId) ?? null, [clients, clientId])
  const mine = useMemo(
    () => contacts.filter((c) => c.client_id === clientId).sort((a, b) => a.position - b.position),
    [contacts, clientId],
  )
  const keys = useMemo(
    () => accesses.filter((a) => a.client_id === clientId),
    [accesses, clientId],
  )

  if (!client) {
    return (
      <>
        <Bar title="Клиент" onBack={onBack} />
        <div className="m-empty">Клиент не найден в загруженных данных</div>
      </>
    )
  }

  return (
    <>
      <Bar title={client.name} onBack={onBack} />

      {(client.contact_person || client.phone || client.email || client.inn) && (
        <div className="m-block" style={{ marginTop: 16 }}>
          <div className="m-block-title">Клиент</div>
          <div className="m-card">
            {client.inn && <KV k="ИНН" v={client.inn} />}
            {client.contact_person && <KV k="Контакт" v={client.contact_person} />}
            {client.phone && <KV k="Телефон" v={client.phone} tel />}
            {client.email && <KV k="Почта" v={client.email} mail />}
          </div>
        </div>
      )}

      {mine.length > 0 && (
        <div className="m-block">
          <div className="m-block-title">Контактные лица</div>
          <div className="m-list" style={{ margin: 0 }}>
            {mine.map((c) => (
              <div key={c.id} className="m-card">
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{ flexGrow: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 15, fontWeight: 700 }}>{c.full_name}</div>
                    {c.role && <div className="m-sub" style={{ marginTop: 2 }}>{c.role}</div>}
                  </div>
                  {c.phone && (
                    <a className="m-iconbtn" href={`tel:${c.phone.replace(/[^\d+]/g, '')}`} aria-label="Позвонить">
                      <IconPhone />
                    </a>
                  )}
                </div>
                {c.phone && <div className="m-sub" style={{ marginTop: 6 }}>{c.phone}</div>}
                {c.email && <div className="m-sub" style={{ marginTop: 2 }}>{c.email}</div>}
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="m-block">
        <div className="m-block-title">Доступы</div>
        {!unlocked() ? (
          <div className="m-card m-sub">
            Доступы зашифрованы кодом входа. Введите код при запуске программы,
            чтобы их увидеть.
          </div>
        ) : keys.length === 0 ? (
          <div className="m-card m-sub">Доступов нет</div>
        ) : (
          <div className="m-list" style={{ margin: 0 }}>
            {keys.map((a) => (
              <AccessBlock key={a.id} access={a} onToast={onToast} />
            ))}
          </div>
        )}
      </div>

      <div style={{ height: 24 }} />
    </>
  )
}

function AccessBlock({ access, onToast }: { access: Access; onToast: (t: string) => void }) {
  return (
    <div className="m-card">
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 10 }}>
        <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--m-navy)' }}>{access.title}</div>
        <span className="m-chip m-chip-est">{ACCESS_KIND_LABEL[access.kind]}</span>
      </div>

      {access.server_address && <Secret label="Адрес" value={access.server_address} onToast={onToast} />}
      {access.login && <Secret label="Логин" value={access.login} onToast={onToast} />}
      {access.password && <Secret label="Пароль" value={access.password} hidden onToast={onToast} />}

      {access.rows.map((r, i) => (
        <div key={r.id ?? i} style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid var(--m-line)' }}>
          {r.computer && <div className="m-sub" style={{ fontWeight: 700 }}>{r.computer}</div>}
          {r.product_name && <div className="m-sub">{r.product_name}</div>}
          {r.url && <Secret label="Ссылка" value={r.url} onToast={onToast} />}
          {r.user_name && <div className="m-sub" style={{ marginTop: 4 }}>{r.user_name}</div>}
          {r.login && <Secret label="Логин" value={r.login} onToast={onToast} />}
          {r.password && <Secret label="Пароль" value={r.password} hidden onToast={onToast} />}
        </div>
      ))}

      {access.recovery_phone && <Secret label="Телефон восстановления" value={access.recovery_phone} onToast={onToast} />}
      {access.recovery_email && <Secret label="Почта восстановления" value={access.recovery_email} onToast={onToast} />}
      {access.note && <div className="m-sub" style={{ marginTop: 8, whiteSpace: 'pre-wrap' }}>{access.note}</div>}
    </div>
  )
}

/**
 * Строка доступа. Пароль закрыт, пока его не открыли: техник часто смотрит
 * телефон при клиенте, и чужой пароль не должен светиться на экране сам собой.
 */
function Secret({ label, value, hidden = false, onToast }: {
  label: string
  value: string
  hidden?: boolean
  onToast: (t: string) => void
}) {
  const [shown, setShown] = useState(!hidden)

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value)
      onToast(`${label} скопирован`)
    } catch {
      onToast('Не удалось скопировать')
    }
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
      <div style={{ flexGrow: 1, minWidth: 0 }}>
        <div className="m-kv-k" style={{ fontSize: 12 }}>{label}</div>
        <div className="m-secret">{shown ? value : '••••••••'}</div>
      </div>
      {hidden && (
        <button className="m-iconbtn" onClick={() => setShown((s) => !s)} aria-label="Показать">
          <IconEye />
        </button>
      )}
      <button className="m-iconbtn" onClick={copy} aria-label="Копировать">
        <IconCopy />
      </button>
    </div>
  )
}

function KV({ k, v, tel, mail }: { k: string; v: string; tel?: boolean; mail?: boolean }) {
  const href = tel ? `tel:${v.replace(/[^\d+]/g, '')}` : mail ? `mailto:${v}` : null
  return (
    <div className="m-kv">
      <span className="m-kv-k">{k}</span>
      <span className="m-kv-v">
        {href ? <a href={href} style={{ color: '#2f739d' }}>{v}</a> : v}
      </span>
    </div>
  )
}
